import { useFormContext } from 'react-hook-form'
import { IntegrationSettings } from '../../../schema'
import { Col, Row } from 'react-bootstrap'
import { SelectDropdown, SelectOption } from '../../../../../../../components/SelectDropdown/SelectDropdown'
import { getIntegrationDisplayName, IntegrationServiceId } from '../../../../../../../common/integrations'
import { MappingType, OperationType } from '../../../../../../../models/Account'
import { useFilterIntegrationMappingTemplatesQuery, useGetSupportedObjectsQuery } from '../../../../../../../store/integrations/integrations'
import { useMemo } from 'react'
import { selectAccountId } from '../../../../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../../../../store/hooks'
import { useGetIntegrationAdminSettings } from '../../../hook'
import { useGetSchemaErrors } from '../../../schemaErrors'

interface SelectSessionTypeSectionProps {
  onChange: () => void
  serviceId: IntegrationServiceId
  disabled?: boolean
}

export const SelectSessionTypeSection: React.FC<SelectSessionTypeSectionProps> = ({ onChange, serviceId, disabled }) => {
  const { watch, setValue } = useFormContext<IntegrationSettings>()
  const accountId = useAppSelector(selectAccountId)
  const currentObjectName = watch('mappings.session.objectName')
  const currentMappingType = watch('mappings.session.mappingType')
  const isCustom = useMemo(() => currentMappingType === MappingType.CUSTOM, [currentMappingType])

  const { data: outboundMappingTemplatePaths, isFetching: isOutboundMappingTemplatePathsFetching } = useFilterIntegrationMappingTemplatesQuery({
    active: true,
    destinationPlatform: serviceId,
    sourceObject: 'meeting',
  })

  const meetingCRMTypeOptions: SelectOption[] = useMemo(() => {
    if (!outboundMappingTemplatePaths) {
      return []
    }
    return outboundMappingTemplatePaths.map((i) => ({ id: i.destinationObject, label: i.name }))
  }, [outboundMappingTemplatePaths])

  const { hasIntegration } = useGetIntegrationAdminSettings(serviceId)

  const { data: supportedObjects, isLoading: isLoadingSupportedObjects } = useGetSupportedObjectsQuery(
    { accountId, connector: serviceId },
    { skip: !accountId || !serviceId || !hasIntegration },
  )

  const customMeetingOption = useMemo(() => supportedObjects?.find((opt) => opt.name === currentObjectName) || null, [supportedObjects, currentObjectName])

  const predefinedMeetingOption: SelectOption | null = useMemo(() => {
    if (isCustom) {
      return { id: 'customMeetingObject', label: 'Custom Meeting Object' }
    }
    return meetingCRMTypeOptions.find((opt) => opt.id === currentObjectName) || null
  }, [meetingCRMTypeOptions, currentObjectName, isCustom])

  const customMeetingOptions: SelectOption[] = useMemo(() => {
    return supportedObjects?.map((obj) => ({ id: obj.name, label: `${obj.label} (${obj.name})` })) || []
  }, [supportedObjects])

  const handleCustomOptionSelect = (option: { id: string; label: string }) => {
    if (option.id === currentObjectName && isCustom) {
      return
    }
    const optionLabel = supportedObjects?.find((obj) => obj.name === option.id)?.label || null
    setValue(
      'mappings.session',
      { objectName: option.id, displayName: optionLabel, operationType: OperationType.CREATE },
      { shouldDirty: true, shouldValidate: true },
    )
    setValue('mappings.session.mappingType', MappingType.CUSTOM, {
      shouldDirty: true,
    })
    onChange()
  }

  const handlePredefinedOptionSelect = (option: { id: string; label: string }) => {
    if (option.id === currentObjectName && !isCustom) {
      return
    }

    if (option.id === 'customMeetingObject') {
      setValue('mappings.session', { objectName: null }, { shouldDirty: true, shouldValidate: true })
      setValue('mappings.session.mappingType', MappingType.CUSTOM, {
        shouldDirty: true,
        shouldValidate: true,
      })
      onChange()

      return
    }
    setValue('mappings.session', { objectName: option.id }, { shouldDirty: true, shouldValidate: true })
    setValue('mappings.session.mappingType', MappingType.PREDEFINED, {
      shouldDirty: true,
      shouldValidate: true,
    })
    onChange()
  }

  const { getErrorOnPath } = useGetSchemaErrors()

  const fieldError = getErrorOnPath('mappings.session.objectName')
  const objectError = getErrorOnPath('mappings.session')

  return (
    <>
      <Row className='mb-2'>
        <Col>
          <h4>Select Object Type*</h4>
          <p className='zocks-text-gray-600'>Select the {getIntegrationDisplayName(serviceId)} object where Meeting Summary will be saved.</p>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <SelectDropdown
            initialSelectedOption={predefinedMeetingOption}
            options={meetingCRMTypeOptions}
            onOptionSelected={handlePredefinedOptionSelect}
            loading={isOutboundMappingTemplatePathsFetching}
            disabled={disabled}
          />
          {fieldError && <div className='text-danger mt-1 small'>{fieldError}</div>}
          {objectError && <div className='text-danger mt-1 small'>{objectError}</div>}
        </Col>
        {isCustom && (
          <Col md={6}>
            <SelectDropdown
              options={customMeetingOptions}
              initialSelectedOption={
                customMeetingOption ? { id: customMeetingOption.name, label: `${customMeetingOption.label} (${customMeetingOption.name})` } : null
              }
              placeholder='Select Object Type'
              onOptionSelected={handleCustomOptionSelect}
              searchable
              loading={isLoadingSupportedObjects}
              disabled={disabled}
            />
          </Col>
        )}
      </Row>
    </>
  )
}
